import { differenceInMinutes, format } from 'date-fns'
import { t } from 'i18next'
import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { FaInfoCircle } from 'react-icons/fa'
import { useSelector } from 'react-redux'

import { useUnapprovedDocumentsQuery } from '../../../api/services/protocolApi'
import { ProcessIcon } from '../../../assets/icons'
import NewModal from '../../root/modals/NewModal'
import NewTable from '../../table/NewTable'
import FileProcessModal from './FileProcessModal'

// Time the document has been waiting at the current approver, e.g. "3 d 4 h", "2 h 15 min", "40 min"
const formatWaiting = (since) => {
  if (!since) return '-'
  const minutes = Math.max(0, differenceInMinutes(new Date(), new Date(since)))
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  if (days > 0) return `${days} ${t('days_short')} ${hours % 24} ${t('hours_short')}`
  if (hours > 0) return `${hours} ${t('hours_short')} ${minutes % 60} ${t('minutes_short')}`
  return `${minutes} ${t('minutes_short')}`
}

// Rendered in a portal so the table's overflow container does not clip it
const Tooltip = ({ text, children }) => {
  const [position, setPosition] = useState(null)

  const show = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const right = window.innerWidth - rect.right
    // Open above the trigger when there is not enough room below it
    setPosition(
      rect.bottom + 160 > window.innerHeight
        ? { bottom: window.innerHeight - rect.top + 8, right }
        : { top: rect.bottom + 8, right },
    )
  }

  return (
    <span className="cursor-help" onMouseEnter={show} onMouseLeave={() => setPosition(null)}>
      {children}
      {position &&
        createPortal(
          <div
            style={position}
            className="fixed z-[100] max-w-xs rounded bg-secondary px-3 py-2 text-left text-sm font-normal normal-case text-white shadow-lg pointer-events-none"
          >
            {text}
          </div>,
          document.body,
        )}
    </span>
  )
}

const UnapprovedDocumentsModal = ({ open, onClose }) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [filterInputs, setFilterInputs] = useState({})
  const [openProcess, setOpenProcess] = useState(false)
  const [processFileId, setProcessFileId] = useState('')

  const company = useSelector((state) => state.company.company)

  const query = useMemo(() => {
    let queryString = ''
    for (const [key, value] of Object.entries(filterInputs)) {
      if (value.trim() !== '') {
        queryString += `${key}=${encodeURIComponent(value.trim())}&`
      }
    }
    return queryString.replace(/&$/, '')
  }, [filterInputs])

  const hasFilters = query.length > 0

  const { data, isLoading, isFetching } = useUnapprovedDocumentsQuery(
    { companyId: company?.id, page, pageSize, query },
    { skip: !open || !company?.id, refetchOnMountOrArgChange: true },
  )

  const rows = useMemo(() => (Array.isArray(data?.rows) ? data.rows : []), [data])

  const columns = useMemo(
    () => [
      {
        Header: t('protocol_number'),
        id: 'Sifra',
        accessor: 'stampKey',
        disableSortBy: true,
      },
      {
        Header: t('partner'),
        id: 'Klijent',
        accessor: 'partner',
        disableSortBy: true,
      },
      {
        Header: 'Status',
        accessor: 'approvedCount',
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="flex flex-col items-center gap-1">
            <div className="bg-primary text-white rounded-lg font-thin w-full">{t('awaiting')}</div>
            <p className="mb-0 text-sm">
              {t('approved_progress', {
                approved: Number(row.original.approvedCount) || 0,
                total: Number(row.original.totalApprovers) || 0,
              })}
            </p>
          </div>
        ),
      },
      {
        Header: t('sent_to'),
        accessor: 'currentApprovers',
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ value }) => value || '-',
      },
      {
        Header: () => (
          <div className="inline-flex items-center gap-2">
            {t('waiting_time')}
            <Tooltip text={t('waiting_time_info')}>
              <FaInfoCircle size={16} />
            </Tooltip>
          </div>
        ),
        accessor: 'waitingSince',
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ value }) =>
          value ? (
            <Tooltip text={`${t('waiting_since')}: ${format(new Date(value), 'dd.MM.yyyy HH:mm')}`}>
              {formatWaiting(value)}
            </Tooltip>
          ) : (
            '-'
          ),
      },
    ],
    [t],
  )

  const tableHooksArray = [
    {
      label: t('process'),
      Icon: ProcessIcon,
      onClick: (row) => {
        setProcessFileId(row.original.id)
        setOpenProcess(true)
      },
      disabled: false,
    },
  ]

  const handleClose = () => {
    setPage(1)
    setFilterInputs({})
    onClose()
  }

  return (
    <NewModal
      showModal={open}
      setShowModal={handleClose}
      onClickClose={handleClose}
      title={t('unapproved_docs')}
      page="report"
      contentScroll={false}
    >
      {!isLoading && data?.count === 0 && !hasFilters ? (
        <p className="text-secondary">{t('no_unapproved_docs')}</p>
      ) : (
        <NewTable
          columns={columns}
          data={rows}
          isLoading={isLoading}
          isFetching={isFetching}
          setPage={setPage}
          pageManual={page}
          pageSize={pageSize}
          setPageAmount={setPageSize}
          controlledPageCount={data?.totalPages || 0}
          count={data?.count}
          filterInputs={filterInputs}
          setFilterInputs={setFilterInputs}
          hooksArray={tableHooksArray}
          maxHeight="max-h-[65vh]"
          actions
          filtering
        />
      )}
      <FileProcessModal open={openProcess} fileId={processFileId} onClose={() => setOpenProcess(false)} />
    </NewModal>
  )
}

export default UnapprovedDocumentsModal
